import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ReactTable from "../ReactTable/TableVisitRouteSticky2";
import ProductEditDialog from "./ProductEditDialog";
import ProductGroupFilter from "./StockCountFilter/ProductGroupFilter";
import ProductTypeFilter from "./StockCountFilter/ProductTypeFilter";
import ProductLineFilter from "./StockCountFilter/ProductLineFilter";
import ItemGroupFilter from "./StockCountFilter/ItemGroupFilter";
import axios from "axios";
import moment from "moment";
import { useSelector } from "react-redux";
import { translator } from "../../utils/Translator";

// === API Service ===
const fetchStockCardData = async (
  company,
  customerCode,
  productCode,
  salesmanCode = ""
) => {
  const token = localStorage.getItem("x-access-token");

  const params = {
    company: company || "81",
    customerCode: customerCode || "PKTBK01A0002",
    productCode: productCode || "", //  ใช้ค่าจาก parameter (ไม่ hardcode)
    salesmanCode: salesmanCode || "",
    language: "1",
  };

  const url = `${process.env.REACT_APP_API_URL}CustomerAccount/getstockcardforui`;


  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      params: params,
    });


    if (!response.data || !response.data.data) {
      console.warn(" API returned no data");
      return { data: [] };
    }

    return response.data;
  } catch (error) {
    console.error(" API Error:", error.response?.data || error.message);
    console.error(" Full Error:", error);
    throw error;
  }
};

// === Dialog ยืนยันสั่งซื้อ ===
const OrderConfirmDialog = ({
  open,
  onClose,
  selectedProducts,
  onConfirm,
  loading,
}) => {
  const classes = useStyles();
  const { translate } = useSelector(({ i18n }) => i18n);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ background: "#f97316", color: "white" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            {translator(translate.ISA_OrderConfirmTitle, "ยืนยันการสั่งซื้อ")}
          </Typography>
          <IconButton onClick={onClose} size="small" style={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: 32 }}>
        <Typography variant="h6" gutterBottom>
          {translator(
            translate.ISA_SelectedProductsList,
            "รายการสินค้าที่เลือก"
          )} ({selectedProducts.length})
        </Typography>
        <Card>
          <CardContent style={{ maxHeight: 400, overflowY: "auto" }}>
            {selectedProducts.map((p, i) => (
              <Box
                key={i}
                p={2}
                mb={i < selectedProducts.length - 1 ? 1.5 : 0}
                border="1px solid #e2e8f0"
                borderRadius={2}
                bgcolor="white"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography fontWeight={600}>
                      {p.ชื่อสินค้า || p.สินค้า}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {translator(translate.ISA_Code, "รหัส")}: {p.สินค้า}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${translator(translate.ISA_NewOrder, "สั่งใหม่")}: ${p.สั่งใหม่}`}
                    style={{ backgroundColor: "#dcfce7", color: "#166534" }}
                  />
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
        <Box
          mt={3}
          p={2}
          bgcolor="#fef3c7"
          borderRadius={3}
          border="1px solid #fcd34d"
        >
          <Typography variant="body2" color="#92400e">
            {translator(
              translate.ISA_ClickConfirmToCreateOrder,
              "กดยืนยันเพื่อสร้างใบสั่งซื้อ"
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        style={{ padding: "0 32px 32px", justifyContent: "center", gap: 16 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          className={classes.secondaryButton}
        >
          {translator(translate.ISA_Cancel1, "ยกเลิก")}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          className={classes.primaryButton}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            translator(translate.ISA_Confirm, "ยืนยัน")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// === Styles ===
const useStyles = makeStyles((theme) => ({
  label: {
    fontWeight: 500,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(0.5),
  },
  modernButton: {
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    padding: "10px 24px",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    },
  },
  primaryButton: {
    background: "#f97316",
    color: "white",
    "&:hover": { background: "#ea580c" },
  },
  secondaryButton: {
    backgroundColor: "#f8fafc",
    color: "#475569",
    border: "1px solid #e2e8f0",
  },
}));

// === Main Dialog ===
const SalesTargetDialog = ({ open, onClose, customerCode, productCode }) => {
  const classes = useStyles();
  const { authUser } = useSelector(({ auth }) => auth);
  const { translate } = useSelector(({ i18n }) => i18n);

  // Redux state สำหรับ filters
  const { productGroupSalesHistorySelected } = useSelector(
    ({ customerFocus }) => customerFocus || {}
  );
  const { productTypeSalesHistorySelected } = useSelector(
    ({ customerFocus }) => customerFocus || {}
  );
  const { productLineSalesHistorySelected } = useSelector(
    ({ customerFocus }) => customerFocus || {}
  );
  const { itemGroupSalesHistorySelected } = useSelector(
    ({ customerFocus }) => customerFocus || {}
  );

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]); // ข้อมูลที่ผ่าน filter แล้ว
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchText, setSearchText] = useState(""); // สำหรับค้นหาสินค้า

  // === ดึงข้อมูลจาก API ใหม่ ===
  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setLoading(true);
      try {

        const result = await fetchStockCardData(
          authUser?.company,
          customerCode,
          productCode || "",
          authUser?.salesman || ""
        );


        // ตรวจสอบ API response structure
        if (!result || !result.success) {
          console.error(" API returned error:", result?.errors);
          alert(
            `${translator(translate.ISA_APIError, "API Error")}: ` +
              (result?.errors?.[0]?.message ||
                translator(translate.ISA_UnknownError, "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ"))
          );
          setData([]);
          return;
        }

        if (!result.data || result.data.length === 0) {
          console.warn(" No data returned from API");
          alert(
            translator(
              translate.ISA_Msg_NoStockCardData,
              "ไม่พบข้อมูล Stock Card สำหรับลูกค้านี้"
            ) +
              "\n" +
              translator(
                translate.ISA_PleaseCheckApiFormattedDataLogic,
                "กรุณาตรวจสอบว่า API ได้ uncomment logic ใน formattedData.map() แล้วหรือยัง"
              )
          );
          setData([]);
          return;
        }

        // Map ข้อมูลจาก API (กรองเฉพาะที่ไม่ null)
        const mapped = result.data
          .filter((item) => item != null) //  กรอง null/undefined ออก
          .map((item, idx) => {

            //  Null-safe: ดึงราคาจากหลายแหล่ง
            const priceFromApi = Number(
              item?.ราคาขาย1 ||
                item?.ราคาขาย2 ||
                item?.ราคาขาย3 ||
                item?.ราคาขาย4 ||
                item?.Price ||
                item?.Price1 ||
                0
            );

            //  Null-safe: ดึงรหัสสินค้า
            const productCodeFromApi =
              item?.รหัสสินค้า ||
              item?.สินค้า ||
              item?.ProductCode ||
              item?.productCode ||
              "";
            const productNameFromApi =
              item?.ชื่อสินค้า || item?.ProductName || "";


            return {
              id: idx,
              สินค้า: productCodeFromApi,
              productCode: productCodeFromApi,
              ชื่อสินค้า: productNameFromApi,
              รหัสสินค้า: productCodeFromApi,
              price: priceFromApi,

              // === ข้อมูล 2 เดือนที่แล้ว (กันยายน 2025) ===
              "2 เดือนที่แล้ว_ยกมา": Number(item?.["2 เดือนที่แล้ว_ยกมา"]) || 0,
              "2 เดือนที่แล้ว_ซื้อ": Number(item?.["2 เดือนที่แล้ว_ซื้อ"]) || 0,
              "2 เดือนที่แล้ว_ขาย": Number(item?.["2 เดือนที่แล้ว_ขาย"]) || 0,

              // === ข้อมูลเดือนที่แล้ว (ตุลาคม 2025) ===
              เดือนที่แล้ว_ยกมา: Number(item?.["เดือนที่แล้ว_ยกมา"]) || 0,
              เดือนที่แล้ว_ซื้อ: Number(item?.["เดือนที่แล้ว_ซื้อ"]) || 0,
              เดือนที่แล้ว_ขาย: Number(item?.["เดือนที่แล้ว_ขาย"]) || 0,

              // === ข้อมูลเดือนล่าสุด (พฤศจิกายน 2025) ===
              เดือนล่าสุด_ยกมา: Number(item?.["เดือนล่าสุด_ยกมา"]) || 0,
              เดือนล่าสุด_ซื้อ: Number(item?.["เดือนล่าสุด_ซื้อ"]) || 0,
              เดือนล่าสุด_ขาย: Number(item?.["เดือนล่าสุด_ขาย"]) || 0,

              // === สรุป ===
              นับ: Number(item?.["นับจริง"]) || 0,
              สต๊อกลูกค้า:
                Number(item?.["สต๊อกในระบบ"]) ||
                Number(item?.["สต๊อกอื่น"]) ||
                0,
              แนะนำ: Number(item?.["แนะนำสั่ง"]) || 0,
              สั่งใหม่: 0, // ค่าเริ่มต้น
            };
          });


        setData(mapped);
        setFilteredData(mapped); // ตั้งค่าเริ่มต้นให้ filteredData = data ทั้งหมด
      } catch (err) {
        console.error(" Load error:", err);
        console.error(" Error details:", err.response?.data);
        alert(
          translator(
            translate.ISA_CannotLoadStockCard,
            "ไม่สามารถโหลดข้อมูล Stock Card ได้"
          ) +
            ":\n" +
            (err.response?.data?.message || err.message) +
            "\n\n" +
            translator(
              translate.ISA_PleaseOpenConsoleForDetails,
              "กรุณาเปิด Console (F12) เพื่อดูรายละเอียด"
            )
        );
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [open, customerCode, productCode, authUser]);

  useEffect(() => {
    if (data.length > 0) {
    } else {
      console.warn(" No data in state!");
    }
  }, [data]);

  // === ฟังก์ชันค้นหาและกรองข้อมูล ===
  const handleSearch = () => {

    let filtered = [...data];

    // Filter by Product Group
    if (
      productGroupSalesHistorySelected &&
      productGroupSalesHistorySelected.length > 0
    ) {
      const selectedValues = productGroupSalesHistorySelected.map(
        (item) => item.value
      );
      filtered = filtered.filter(
        (item) =>
          selectedValues.includes(item.กลุ่มสินค้า) ||
          selectedValues.includes(item.ProductGroup)
      );
    }

    // Filter by Product Type
    if (
      productTypeSalesHistorySelected &&
      productTypeSalesHistorySelected.length > 0
    ) {
      const selectedValues = productTypeSalesHistorySelected.map(
        (item) => item.value
      );
      filtered = filtered.filter(
        (item) =>
          selectedValues.includes(item.ประเภทสินค้า) ||
          selectedValues.includes(item.ProductType)
      );
    }

    // Filter by Product Line
    if (
      productLineSalesHistorySelected &&
      productLineSalesHistorySelected.length > 0
    ) {
      const selectedValues = productLineSalesHistorySelected.map(
        (item) => item.value
      );
      filtered = filtered.filter(
        (item) =>
          selectedValues.includes(item.ไลน์สินค้า) ||
          selectedValues.includes(item.ProductLine)
      );
    }

    // Filter by Item Group
    if (
      itemGroupSalesHistorySelected &&
      itemGroupSalesHistorySelected.length > 0
    ) {
      const selectedValues = itemGroupSalesHistorySelected.map(
        (item) => item.value
      );
      filtered = filtered.filter(
        (item) =>
          selectedValues.includes(item.หมวดสินค้า) ||
          selectedValues.includes(item.ItemGroup)
      );
    }

    // Filter by Search Text (รหัสสินค้า หรือ ชื่อสินค้า)
    if (searchText && searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const productCode = (
          item.รหัสสินค้า ||
          item.สินค้า ||
          ""
        ).toLowerCase();
        const productName = (item.ชื่อสินค้า || "").toLowerCase();
        return (
          productCode.includes(searchLower) || productName.includes(searchLower)
        );
      });
    }

    setFilteredData(filtered);
    setSelectedRows([]); // Clear selection เมื่อ filter ใหม่
  };

  // === Reset Filter ===
  const handleResetFilter = () => {
    setSearchText("");
    setFilteredData(data);
    setSelectedRows([]);
  };

  // === สร้างเลขที่เอกสาร ===
  const genarateDocNumber = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}CustomerAccount/genarateDocNumber`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("x-access-token")}`,
          },
          params: {
            company: authUser?.company || "DEFAULT",
            documentType: "DOC_SLN_VAT",
            salesmanCode: authUser?.salesman || authUser?.username || "DEFAULT",
          },
        }
      );

      // Debug log to inspect the full response

      // Access the correct path for docNumber
      if (response.data && response.data.data && response.data.data.docNumber) {
        return response.data.data.docNumber;
      } else {
        console.warn(" API did not return a valid DocNumber");
        throw new Error(
          translator(
            translate.ISA_CannotGenerateDocumentNumber,
            "ไม่สามารถสร้างเลขที่เอกสารได้"
          )
        );
      }
    } catch (error) {
      console.error(" Error generating document number:", error);
      alert(
        `${translator(
          translate.ISA_ErrorGeneratingDocumentNumber,
          "เกิดข้อผิดพลาดในการสร้างเลขที่เอกสาร"
        )}: ` + (error.response?.data?.message || error.message)
      );
      return null;
    }
  };

  // === แก้ไขสินค้า ===
  const handleEdit = (product) => {
    setSelectedProduct({
      ...product,
      company: authUser?.company,
      customerCode: customerCode || "KCLTC00611",
      salesmanCode: authUser?.salesman,
      onSave: handleEditSave, // ส่งฟังก์ชัน callback ไปด้วย
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = (updatedProduct) => {

    // อัปเดตข้อมูลในตาราง
    setData((prev) =>
      prev.map((p) =>
        p.id === updatedProduct.id
          ? {
              ...p,
              สั่งใหม่: updatedProduct.สั่งใหม่,
              quantities: updatedProduct.quantities, // เก็บรายละเอียดด้วย
            }
          : p
      )
    );

    setEditDialogOpen(false);

    // แสดงข้อความยืนยัน
  };

  // === สั่งซื้อ ===
  const handleOrderNow = () => {
    const selected = filteredData.filter((_, i) => selectedRows.includes(i));
    if (selected.length === 0) {
      return alert(translator(translate.ISA_ChooseProduct, "กรุณาเลือกสินค้า"));
    }
    setOrderDialogOpen(true);
  };

  const handleConfirmOrder = async () => {
    setOrderLoading(true);

    try {
      // 1. สร้างเลขที่เอกสาร
      const docNumber = await genarateDocNumber();
      if (!docNumber) {
        throw new Error(
          translator(
            translate.ISA_CannotGenerateDocumentNumber,
            "ไม่สามารถสร้างเลขที่เอกสารได้"
          )
        );
      }


      const selected = filteredData.filter((_, i) => selectedRows.includes(i));

      // 2. สร้าง transactionItemData (ตาม logic จาก DocumentForm.jsx)
      const transactionItemData = [];
      let itemNumber = 1;

      selected.forEach((product) => {
        // ดึงข้อมูล quantities จาก product ที่ถูกแก้ไข
        let quantities = product.quantities;

        // ตรวจสอบว่า quantities เป็น array หรือไม่
        if (!Array.isArray(quantities)) {
          quantities = [
            {
              unit: translator(translate.ISA_UnitCrate, "ลัง"),
              unitQty: 1,
              quantity: parseFloat(product.สั่งใหม่) || 0,
              price: 0,
              totalPrice: 0,
            },
          ];
        }


        // แปลง quantities เป็น transactionItem
        quantities.forEach((item) => {

          transactionItemData.push({
            ItemNumber: itemNumber,
            DiscountRefNo: itemNumber,
            ProductCode: product.สินค้า || "",
            ProductQuan: item.quantity || 0,
            ProductUnit: item.unit || translator(translate.ISA_UnitBox, "กล่อง"),
            ProductPrice: product.price || 0,
            ItemCategory: "O", // O = Order, F = Free
            ItemRemark: `${translator(
              translate.ISA_StockCardOrderNewOrderRemark,
              "Stock Card Order - สั่งใหม่: "
            )}${product.สั่งใหม่}`,
            ItemMFGDate: "",
            ItemPromotionCode: "",
            ItemDepartmentCode: "",
            ProductSetCode: "",
            HighestUnitPrice: 0,
            SalesAgreementNumber: "",
            LineDiscountAmount: 0,
            DeliveryDate: moment().format("YYYY-MM-DD"),
            ShipToCode: "",
            BalanceQuan: item.quantity || 0,
          });

          itemNumber++;
        });
      });


      // 3. สร้าง transactionDiscountData (ตาม logic จาก DocumentForm.jsx)
      const transactionDiscountData = [];
      const transactionDiscountTransportationData = [];
      const transactionAddingData = [];
      const transactionExtraData = [];

      // 4. คำนวณยอดรวม (ตาม logic จาก DocumentForm.jsx)
      let totalSum = 0;
      let totalDiscount = 0;
      let totalVat = 0;
      let subTotal = 0;
      let grandTotal = 0;

      transactionItemData.forEach((item) => {
        const itemTotal = (item.ProductQuan || 0) * (item.ProductPrice || 0);
        totalSum += itemTotal;
        totalDiscount += item.LineDiscountAmount || 0;
      });

      subTotal = totalSum - totalDiscount;
      totalVat = subTotal * 0.07; // VAT 7%
      grandTotal = subTotal + totalVat;

      // 5. สร้าง Transaction Payload (ตาม logic จาก DocumentForm.jsx)
      const transactionData = {
        SalesmanCode: authUser?.salesman || authUser?.username || "DEFAULT",
        Username: authUser?.username || "DEFAULT",
        CustomerCode: customerCode || "DEFAULT",
        DocDate: moment().format("YYYY-MM-DD"),
        DocNumber: docNumber, // ใช้เลขที่เอกสารที่สร้างขึ้น
        DocRefNumber: "",
        ShipDate: moment().format("YYYY-MM-DD"),
        Bill_ShipCode: customerCode || "DEFAULT",
        Bill_ShipName: "Auto Generated from Stock Card",
        Bill_ShipAddress: "",
        Bill_ShipProvinceCode: "",
        ShipCode: customerCode || "DEFAULT",
        ShipName: "Auto Generated from Stock Card",
        ShipAddress: "",
        ShipProvinceCode: "",
        ShipToPrint: "",
        DocRemark: "Order from Stock Card - Auto Generated",
        Total_Discount: totalDiscount,
        Total_Sum: totalSum,
        Total_HeadDiscount: 0,
        Total_Vat: totalVat,
        Total_Total: subTotal,
        Total_CashDiscountP: 0,
        Total_CashDiscountB: 0,
        Total_GrandTotal: grandTotal,
        SalesOrg: authUser?.salesOrg || "",
        SalesChannel: authUser?.salesChannel || "",
        Company: authUser?.company || "DEFAULT",
        ReturnReasonCode: "",
        BillRefNo: "",
        DocumentType: "DOC_SLN_VAT",
        docType: "DOC_ORDER",
        DocRefDate: moment().format("YYYY-MM-DD"),
        DocRefDate2: "",
        ContactPerson: "",
        DiscountRefNo: "",
        CreditType: "",
        PaymentTermCode: "",
        BillCurrency: "THB",
        BillExchangeRate: 1,
        DepartmentCode: "",
        DocumentTypeCode: "DOC_SLN_VAT",
        WareHouseCode: "",
        ModeOfDeliveryCode: "",
        Status: "B", // B = Confirmed, D = Draft
        VatType: "EXC", // EXC = Exclude VAT, INC = Include VAT
        transactionItem: transactionItemData,
        transactionDiscount: transactionDiscountData,
        transactionDiscountTransportation: transactionDiscountTransportationData,
        transactionAdding: transactionAddingData,
        transactionExtra: transactionExtraData,
        isDraft: false,
        mode: "CREATE",
      };


      // // Guard: don't call API when totals are zero (prevents invalid payloads causing 500)
      // if ((transactionData.Total_GrandTotal || 0) <= 0 || (transactionItemData.length === 0)) {
      //   console.warn(' Preventing order creation because totals are zero or no items', {
      //     Total_GrandTotal: transactionData.Total_GrandTotal,
      //     transactionItemCount: transactionItemData.length,
      //   });
      //   alert('ไม่สามารถสร้างใบสั่งซื้อ: ยอดรวมหรือรายการสินค้ามีค่าเป็น 0');
      //   setOrderLoading(false);
      //   return;
      // }

      // 6. เรียก API สร้างเอกสาร (ตาม logic จาก DocumentForm.jsx)
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}orderTransaction/create`,
        transactionData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("x-access-token")}`,
            "Content-Type": "application/json",
          },
        }
      );


      // 7. ตรวจสอบ response
      const responseData = response.data;
      const errors = responseData?.errors || [];

      if (
        responseData?.success === true &&
        responseData?.data &&
        errors.length === 0
      ) {
        // สำเร็จ
        setSuccessMessage(
          `${translator(
            translate.ISA_OrderCreateSuccessWithNumber,
            "สร้างใบสั่งซื้อสำเร็จ! เลขที่: "
          )}${docNumber}`
        );
        setShowSuccess(true);
        setOrderDialogOpen(false);
        setSelectedRows([]);

        setTimeout(() => {
          setShowSuccess(false);
          onClose(true); // ส่ง true เพื่อให้รีเฟรชข้อมูล
        }, 2000);
      } else {
        // มี error จาก API
        const errorMessages = errors
          .map((err) => `• ${err.message || err}`)
          .join("\n");
        throw new Error(
          errorMessages ||
            translator(
              translate.ISA_ErrorCreatingDocument,
              "เกิดข้อผิดพลาดในการสร้างเอกสาร"
            )
        );
      }
    } catch (err) {
      console.error(" Order Error:", err);
      console.error(" Error Details:", err.response?.data);
      console.error(" Error Message:", err.message);
      console.error(
        " Full Error Object:",
        JSON.stringify(err.response?.data, null, 2)
      );

      // แสดง error message ที่ละเอียดขึ้น
      let errorMessage = translator(
        translate.ISA_ErrorCreatingOrder,
        "เกิดข้อผิดพลาดในการสร้างใบสั่งซื้อ"
      );

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Build a robust short server-detail string from several possible locations
      let serverDetail = null;
      try {
        if (err?.response) {
          const resp = err.response;
          // Prefer structured messages
          serverDetail = resp.data?.message || resp.data?.error || null;

          // If still empty, try common fields
          if (!serverDetail && resp.data) {
            if (
              Array.isArray(resp.data.errors) &&
              resp.data.errors.length > 0
            ) {
              serverDetail = resp.data.errors
                .map((e) => e.message || e)
                .join("; ");
            } else if (typeof resp.data === "object") {
              // stringify small objects
              try {
                serverDetail = JSON.stringify(resp.data);
              } catch (e) {
                serverDetail = String(resp.data);
              }
            } else {
              serverDetail = String(resp.data);
            }
          }

          // As a last resort include HTTP status
          if (!serverDetail && resp.status) {
            serverDetail = `${resp.status} ${resp.statusText || ""}`.trim();
          }
        }

        if (!serverDetail) serverDetail = err.message || String(err) || null;
      } catch (e) {
        serverDetail =
          err.message ||
          String(err) ||
          translator(
            translate.ISA_NoAdditionalDetails,
            "ไม่พบรายละเอียดเพิ่มเติม"
          );
      }

      const serverDetailStr = serverDetail
        ? String(serverDetail)
        : translator(
            translate.ISA_NoAdditionalDetails,
            "ไม่พบรายละเอียดเพิ่มเติม"
          );
      const snippet =
        serverDetailStr.length > 1000
          ? serverDetailStr.substring(0, 1000) + "..."
          : serverDetailStr;

      alert(
        ` ${errorMessage}\n\n${translator(
          translate.ISA_ShortDetails,
          "รายละเอียดสั้น"
        )}: ${snippet}\n\n(${translator(
          translate.ISA_CheckConsoleForDetails,
          "ตรวจสอบ console สำหรับรายละเอียดทั้งหมด"
        )})`
      );
    } finally {
      setOrderLoading(false);
    }
  };

  // === เลือกแถว ===
  const toggleRow = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const toggleAll = () => {
    setSelectedRows(
      selectedRows.length === filteredData.length
        ? []
        : filteredData.map((_, i) => i)
    );
  };

  // === คอลัมน์ตาราง (อัปเดตตามข้อมูลใหม่) ===
  const columns = useMemo(() => {

    const cols = [
      {
        Header: () => (
          <Checkbox
            size="small"
            checked={
              selectedRows.length === filteredData.length &&
              filteredData.length > 0
            }
            indeterminate={
              selectedRows.length > 0 &&
              selectedRows.length < filteredData.length
            }
            onChange={toggleAll}
          />
        ),
        accessor: "checkbox",
        width: 60,
        Cell: ({ row }) => (
          <Checkbox
            size="small"
            checked={selectedRows.includes(row.index)}
            onChange={() => toggleRow(row.index)}
          />
        ),
      },
      {
        Header: translator(translate.ISA_Product, "สินค้า"),
        accessor: "ชื่อสินค้า",
        width: 200,
        Cell: ({ value, row }) => {
          return <div className="text-left">{value || "-"}</div>;
        },
      },
      {
        Header: translator(translate.ISA_Code, "รหัส"),
        accessor: "รหัสสินค้า",
        width: 120,
        Cell: ({ value }) => {
          return <div className="text-center">{value || "-"}</div>;
        },
      },

      // มิถุนายน 2025 (งวดที่ 3)
      {
        Header: `${translator(translate.ISA_September, "กันยายน")} 2025`,
        columns: [
          {
            Header: translator(translate.ISA_BroughtForward, "ยกมา"),
            accessor: "2 เดือนที่แล้ว_ยกมา",
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["2 เดือนที่แล้ว_ยกมา"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
          {
            Header: translator(translate.ISA_Buy, "ซื้อ"),
            accessor: "2 เดือนที่แล้ว_ซื้อ",
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["2 เดือนที่แล้ว_ซื้อ"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
          {
            Header: translator(translate.ISA_Sell, "ขาย"),
            accessor: "2 เดือนที่แล้ว_ขาย",
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["2 เดือนที่แล้ว_ขาย"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
        ],
      },

      // กรกฎาคม 2025 (งวดที่ 2)
      {
        Header: `${translator(translate.ISA_October, "ตุลาคม")} 2025`,
        columns: [
          {
            Header: translator(translate.ISA_BroughtForward, "ยกมา"),
            accessor: "เดือนที่แล้ว_ยกมา",
            width: 80,
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["เดือนที่แล้ว_ยกมา"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
          {
            Header: translator(translate.ISA_Buy, "ซื้อ"),
            accessor: "เดือนที่แล้ว_ซื้อ",
            width: 80,
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["เดือนที่แล้ว_ซื้อ"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
          {
            Header: translator(translate.ISA_Sell, "ขาย"),
            accessor: "เดือนที่แล้ว_ขาย",
            width: 80,
            Cell: ({ row }) => {
              const value = row.original["เดือนที่แล้ว_ขาย"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
        ],
      },

      // สิงหาคม 2025 (งวดที่ 1 - ล่าสุด)
      {
        Header: `${translator(translate.ISA_November, "พฤศจิกายน")} 2025`,
        columns: [
          {
            Header: translator(translate.ISA_BroughtForward, "ยกมา"),
            accessor: "เดือนล่าสุด_ยกมา",
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["เดือนล่าสุด_ยกมา"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
          {
            Header: translator(translate.ISA_Buy, "ซื้อ"),
            accessor: "เดือนล่าสุด_ซื้อ",
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["เดือนล่าสุด_ซื้อ"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
          {
            Header: translator(translate.ISA_Sell, "ขาย"),
            accessor: "เดือนล่าสุด_ขาย",
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["เดือนล่าสุด_ขาย"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
        ],
      },

      // สรุป
      {
        Header: translator(translate.ISA_Summary, "สรุป"),
        columns: [
          {
            Header: translator(translate.ISA_Count, "นับ"),
            accessor: "นับ",
            width: 80,
            width: 120,
            Cell: ({ row }) => {
              const value = row.original["นับ"];
              return (
                <div style={{ textAlign: "center", padding: "4px" }}>
                  {value !== undefined ? value : "-"}
                </div>
              );
            },
          },
          {
            Header: translator(translate.ISA_CustomerStockLabel, "สต๊อกลูกค้า"),
            accessor: "สต๊อกลูกค้า",
            width: 100,
            Cell: ({ value }) => <div className="text-right">{value}</div>,
          },
          {
            Header: translator(translate.ISA_Recommend, "แนะนำ"),
            accessor: "แนะนำ",
            width: 80,
            Cell: ({ value }) => <div className="text-right">{value}</div>,
          },
          {
            Header: translator(translate.ISA_NewOrder, "สั่งใหม่"),
            accessor: "สั่งใหม่",
            width: 100,
            Cell: ({ value, row }) => {
              const isUpdated = row.original.quantities; // ตรวจสอบว่าถูกอัปเดตแล้วหรือยัง
              return (
                <div className="text-right">
                  <span
                    style={{
                      fontWeight: "bold",
                      color: isUpdated ? "#16a34a" : "#dc2626",
                      backgroundColor: isUpdated ? "#dcfce7" : "#fee2e2",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      display: "inline-block",
                    }}
                  >
                    {value || 0}
                  </span>
                </div>
              );
            },
          },
        ],
      },

      {
        Header: "",
        accessor: "action",
        width: 80,
        Cell: ({ row }) => (
          <Button
            size="small"
            onClick={() => handleEdit(row.original)}
            title={translator(translate.ISA_Edit, "แก้ไข")}
          >
            <EditIcon fontSize="small" />
          </Button>
        ),
      },
    ];

    return cols;
  }, [selectedRows, filteredData]);

  // === Main Render ===
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        PaperProps={{ style: { minHeight: "85vh" } }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6">
                {translator(
                  translate.ISA_ProductStock,
                  "ยอดระบาย"
                )}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {translator(translate.ISA_Customer, "ลูกค้า")}: {" "}
                {customerCode || translator(translate.ISA_NotSpecified, "ไม่ระบุ")} |{" "}
                {translator(translate.ISA_show_more, "แสดง")}: {" "}
                {filteredData.length} {translator(translate.ISA_Items, "รายการ")}{" "}
                {filteredData.length < data.length &&
                  `(${translator(translate.ISA_FromTotalItems, "จากทั้งหมด")} ${data.length})`}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box p={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className={classes.label}>
                {translator(translate.ISA_SearchProduct, "ค้นหาสินค้า")}
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                placeholder={translator(
                  translate.ISA_SearchProductCodeOrName,
                  "ค้นหารหัสสินค้าหรือชื่อสินค้า..."
                )}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            </Grid>
            

            {/* Filter Section */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography className={classes.label}>
                  {translator(translate.ISA_ProductGroup, "กลุ่มสินค้า")}
                </Typography>
                <ProductGroupFilter
                  placeholder={translator(
                    translate.ISA_SelectProductGroup,
                    "เลือกกลุ่มสินค้า"
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography className={classes.label}>
                  {translator(translate.ISA_ProductType, "ประเภทสินค้า")}
                </Typography>
                <ProductTypeFilter
                  placeholder={translator(
                    translate.ISA_SelectProductType,
                    "เลือกประเภทสินค้า"
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography className={classes.label}>
                  {translator(translate.ISA_ProductLine, "ไลน์สินค้า")}
                </Typography>
                <ProductLineFilter
                  placeholder={translator(
                    translate.ISA_SelectProductLine,
                    "เลือกไลน์สินค้า"
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography className={classes.label}>
                  {translator(translate.ISA_ItemCategory, "หมวดสินค้า")}
                </Typography>
                <ItemGroupFilter
                  placeholder={translator(
                    translate.ISA_SelectItemGroup,
                    "เลือกหมวดสินค้า"
                  )}
                />
              </Grid>
            </Grid>

            {/* Search and Action Section */}
            <Grid
              container
              spacing={2}
              alignItems="flex-end"
              style={{ marginTop: 8 }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSearch}
                  style={{
                    backgroundColor: "#bc650eff",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {translator(translate.ISA_Search, "ค้นหา")}
                </Button>
              </Grid>
              {/* <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleResetFilter}
                  style={{
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  ยกเลิก
                </Button>
              </Grid> */}
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                container
                justifyContent="flex-end"
              >
                <Button
                  variant="contained"
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleOrderNow}
                  disabled={selectedRows.length === 0}
                  style={{
                    backgroundColor:
                      selectedRows.length > 0 ? "#f97316" : "#cbd5e1",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {translator(translate.ISA_OrderButton, "สั่งซื้อ")} ({selectedRows.length})
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Add debugging logs to check data and columns before rendering ReactTable */}
          <Box border="2px solid #e4ebf1" borderRadius={1} mt={2}>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={4}
              >
                <CircularProgress />
                <Typography style={{ marginLeft: 16 }}>
                  {translator(translate.ISA_LoadingDot, "กำลังโหลดข้อมูล...")}
                </Typography>
              </Box>
            ) : filteredData.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={4}
              >
                <Typography color="textSecondary">
                  {data.length === 0
                    ? translator(
                        translate.ISA_Msg_NoStockCardData,
                        "ไม่พบข้อมูล Stock Card สำหรับลูกค้านี้"
                      )
                    : translator(
                        translate.ISA_NoSearchResultsTryAgainOrReset,
                        "ไม่พบข้อมูลที่ตรงกับเงื่อนไขการค้นหา กรุณาลองค้นหาใหม่หรือกด Reset"
                      )}
                </Typography>
              </Box>
            ) : (
              <>
                {console.log("Rendering ReactTable with:")}
                {console.log("  - Columns:", columns.length)}
                {console.log("  - Filtered Data rows:", filteredData.length)}
                {console.log("  ", filteredData[0])}
                <ReactTable
                  columns={columns}
                  data={filteredData}
                  loading={loading}
                  initialPageSize={10}
                  stickyHeader
                  style={{ border: "1px solid red", backgroundColor: "#fff" }}
                />
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <ProductEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        productData={selectedProduct}
        onSave={handleEditSave}
      />

      <OrderConfirmDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        selectedProducts={filteredData.filter((_, i) =>
          selectedRows.includes(i)
        )}
        onConfirm={handleConfirmOrder}
        loading={orderLoading}
      />

      <Dialog open={showSuccess} maxWidth="sm" fullWidth>
        <DialogContent style={{ textAlign: "center", padding: 32 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Box
              width={80}
              height={80}
              borderRadius="50%"
              bgcolor="#22c55e"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CheckCircleIcon style={{ fontSize: 48, color: "white" }} />
            </Box>
            <Typography variant="h5" style={{ color: "#166534" }}>
              {translator(translate.ISA_Success, "สำเร็จ")}!
            </Typography>
            <Typography>{successMessage}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SalesTargetDialog;
